import { useState } from 'react';

import { countersApi, type CounterTable } from '@/api/queries/counters';
import { Button, Icon, Input, Modal, useToast } from '@/components/ui';

const SUGGESTED = ['cs', 'sk', 'en', 'de', 'pl', 'fr', 'es', 'it'];

interface Props {
  table: CounterTable;
  onClose: () => void;
}

export function AddLanguageModal({ table, onClose }: Props) {
  const add = countersApi.useAddLanguage();
  const remove = countersApi.useRemoveLanguage();
  const toast = useToast();
  const [picked, setPicked] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);

  const available = SUGGESTED.filter((l) => !table.langs.includes(l));

  const submit = async () => {
    if (!picked) {
      toast.show({ severity: 'error', title: 'Pick a language' });
      return;
    }
    try {
      const res = await add.mutateAsync({ key: table.key, lang: picked, autoTranslate: auto });
      toast.show({
        severity: 'success',
        title: auto
          ? `Queued translation job ${res.jobId ?? ''}. Drafts will appear for review.`
          : `Added language ${picked}`,
      });
      onClose();
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Failed to add language',
      });
    }
  };

  const removeLang = async (lang: string) => {
    if (lang === table.defaultLang) return;
    if (!confirm(`Remove language ${lang}? Values in this language will be lost.`)) return;
    try {
      await remove.mutateAsync({ key: table.key, lang });
      toast.show({ severity: 'success', title: `Removed ${lang}` });
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Failed to remove',
      });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Languages"
      subtitle="Add a language to translate row values into."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" loading={add.isPending} onClick={submit}>
            Add language
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', marginBottom: 6 }}>
            Active languages
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {table.langs.map((l) => (
              <span
                key={l}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 6,
                  background: 'var(--surface-2)',
                }}
              >
                {l}
                {l === table.defaultLang ? (
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>· default</span>
                ) : (
                  <button
                    type="button"
                    aria-label={`Remove ${l}`}
                    onClick={() => removeLang(l)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-3)',
                      padding: 0,
                    }}
                  >
                    <Icon name="x" size={12} />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)', marginBottom: 6 }}>
            Add language
          </div>
          {available.length > 0 ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {available.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setPicked(l)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                    background: picked === l ? 'var(--brand-soft)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--text-3)' }}>
              Every suggested language is already configured. Use the input below for a custom code.
            </p>
          )}
          <Input
            value={picked ?? ''}
            placeholder="ISO 639-1, e.g. pl"
            onChange={(e) => setPicked(e.currentTarget.value)}
            style={{ marginTop: 8, maxWidth: 200 }}
          />
        </div>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={auto}
            onChange={(e) => setAuto(e.currentTarget.checked)}
          />
          <span>
            <Icon name="sparkles" size={12} /> Auto-translate using AI (drafts must be reviewed)
          </span>
        </label>
      </div>
    </Modal>
  );
}
